import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import selectionStorage from '../utils/selectionStorage';
import permissionService from '../services/permissionService';
import accountService from '../services/accountService';
import partnerService from '../services/partnerService';
import hotelService from '../services/hotelService';

// AccountContext manages the selected hotel chain (account) for filtering data across the portal
//
// Hotel Chain (Account) Access:
// - Super Admins (isSuperAdmin = "Y"): Can see and access ALL hotel chains in the system
// - Regular Users: Can only see hotel chains they have a relationship to
//   (User-Account relationship will be implemented in a future update)
//
// The selected account persists in localStorage and filters data throughout the application
const AccountContext = createContext();

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};

// Returns flags indicating what the current partner allows.
// isSuperAdmin bypasses all restrictions (hconnect staff).
export const usePartnerAccess = (isSuperAdmin) => {
  const { partnerData, partnerDataLoaded } = useAccount();
  if (!partnerDataLoaded) return { stripeReady: true, hasVaultProducts: true, partnerData: null, partnerDataLoaded: false };
  const isHconnect = partnerData?.companyname === 'hconnect';
  // skip_stripe replaces what used to be a hardcoded `companyname === 'hconnect'` test here: a partner that
  // never takes a payout (ours, demo, internal) may use the product without connecting Stripe. A display
  // name is not a lookup key — renaming the partner used to silently remove the bypass, and no second
  // partner could be granted it without a code change. See V396, which sets the flag for hconnect.
  const stripeReady = isSuperAdmin || partnerData?.skip_stripe === 'Y' || partnerData?.stripe_onboarding_complete === 1;
  const hasVaultProducts = isSuperAdmin || isHconnect || (partnerData?.has_vault_products > 0);
  return { stripeReady, hasVaultProducts, partnerData, partnerDataLoaded };
};

export const AccountProvider = ({ children }) => {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [isChainAdmin, setIsChainAdmin] = useState(false);
  const [isAnyAdmin, setIsAnyAdmin] = useState(false);
  // Has the admin check come back yet? It starts false and is answered by a round trip, so acting on it
  // before it resolves reads every admin as a plain user — which would bounce a chain admin off their own
  // /vaults/new link on a page refresh.
  const [adminChecked, setAdminChecked] = useState(false);
  const [accountsRefreshKey, setAccountsRefreshKey] = useState(0);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerData, setPartnerData] = useState(null);
  const [partnerDataLoaded, setPartnerDataLoaded] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [hotels, setHotels] = useState([]);

  const { user, loading: authLoading, isSuperAdmin } = useAuth();
  // MAY THIS USER READ THE PARTNER RECORD? GET /partners/{id} needs a grant ON the partner (authz.canAccessPartner):
  // a superadmin, or someone with a partner-level permission. A person at a vendor holds none — the navbar selects
  // the partner they are a vendor FOR, never one they belong to — and nor does a chain or hotel user, so asking
  // only drew a 404 on every page load (HIL-130). Not asked; partnerData stays null, as the 404 left it.
  const canReadPartner = (pid) => !!pid && (isSuperAdmin
    || (user?.partners || []).some(p => String(p.partnerId) === String(pid)));

  // Has the restore-from-localStorage pass below run yet?
  //
  // It cannot run until auth resolves (the saved selection belongs to a specific user and must not be
  // handed to a different one), which on a full page refresh is a round trip. Until then selectedAccount
  // is still its initial null — and "no chain" is indistinguishable from "the user deselected the chain"
  // to any effect watching it. The hotels effect treated it as the latter and DELETED selectedHotelId,
  // so by the time the restore pass read localStorage the hotel was already gone. Partner and chain
  // survived a refresh only because nothing deletes their keys on mount.
  const selectionRestored = useRef(false);

  // Check if user is admin on any hotel or chain (for Support menu visibility)
  useEffect(() => {
    if (localStorage.getItem('idToken')) {
      permissionService.checkAnyAdmin()
        .then(data => setIsAnyAdmin(data.isAnyAdmin === true))
        .catch(() => setIsAnyAdmin(false))
        .finally(() => setAdminChecked(true));
    } else {
      setAdminChecked(true);
    }
  }, []);

  // The selected partner/account/hotel is scoped to the user who chose it.
  // Restore it on refresh for the same user, but drop it whenever a different
  // user logs in (or on logout) so one user's context never leaks into another
  // user's session on a shared browser.
  useEffect(() => {
    if (authLoading) return; // wait until auth is resolved before deciding
    const userKey = user ? (user.sub || user.email || null) : null;
    const owner = selectionStorage.getOwnerSub();

    if (userKey && owner === userKey) {
      // Same user returning (e.g. page refresh) — restore their selection
      // THIS TAB's selection, not the browser's. See utils/selectionStorage — two tabs on
      // different chains shared one localStorage value, so the last tab to touch the picker moved
      // every other tab's requests while their navbars carried on showing something else.
      const savedPartnerId = selectionStorage.getPartnerId();
      const savedAccountId = selectionStorage.getAccountId();
      const savedHotelId = selectionStorage.getHotelId();
      setSelectedPartner(savedPartnerId ? parseInt(savedPartnerId) : null);
      setSelectedAccount(savedAccountId ? parseInt(savedAccountId) : null);
      setSelectedHotel(savedHotelId ? parseInt(savedHotelId) : null);
    } else {
      // Different user, or logged out — discard any stale selection
      setSelectedPartner(null);
      setSelectedAccount(null);
      setSelectedHotel(null);
      selectionStorage.clear();
      selectionStorage.setOwnerSub(userKey || null);
    }
    // Set in BOTH branches: a discarded selection is as restored as a recovered one — from here on a
    // null chain really does mean the user has none, and clearing the saved hotel is correct.
    selectionRestored.current = true;
  }, [authLoading, user]); // eslint-disable-line

  // Fetch partner data whenever selected partner changes
  useEffect(() => {
    if (!selectedPartner || !localStorage.getItem('idToken')) {
      setPartnerData(null);
      setPartnerDataLoaded(!selectedPartner); // loaded (as null) when no partner selected
      return;
    }
    if (!canReadPartner(selectedPartner)) { setPartnerData(null); setPartnerDataLoaded(true); return; }
    setPartnerDataLoaded(false);
    partnerService.getPartnerById(selectedPartner)
      .then(res => { setPartnerData(res.data?.partner || null); setPartnerDataLoaded(true); })
      .catch(() => { setPartnerData(null); setPartnerDataLoaded(true); });
  }, [selectedPartner, isSuperAdmin, user]); // eslint-disable-line

  // Fetch full account data + chain admin status whenever selected account changes
  useEffect(() => {
    if (!selectedAccount || !localStorage.getItem('idToken')) {
      setIsChainAdmin(false);
      setAccountData(null);
      return;
    }
    permissionService.checkChainAdmin(selectedAccount)
      .then(data => setIsChainAdmin(data.isAdmin === true))
      .catch(() => setIsChainAdmin(false));
    accountService.getAccountById(selectedAccount)
      .then(data => setAccountData(data))
      .catch(() => setAccountData(null));
  }, [selectedAccount, accountsRefreshKey]);

  // When a chain is selected (no explicit partner), load partner data from the account's partnerid
  useEffect(() => {
    if (selectedPartner || !localStorage.getItem('idToken')) return;
    const pid = accountData?.account?.partnerid || accountData?.partnerid;
    if (!pid || !canReadPartner(pid)) { setPartnerData(null); setPartnerDataLoaded(true); return; }
    setPartnerDataLoaded(false);
    partnerService.getPartnerById(pid)
      .then(res => { setPartnerData(res.data?.partner || null); setPartnerDataLoaded(true); })
      .catch(() => { setPartnerData(null); setPartnerDataLoaded(true); });
  }, [accountData, selectedPartner, isSuperAdmin, user]); // eslint-disable-line

  // Fetch hotels for the selected chain, clear when chain is deselected
  useEffect(() => {
    if (!selectedAccount || !localStorage.getItem('idToken')) {
      setHotels([]);
      // Only clear the saved hotel once the restore pass has decided what the selection IS. Before that,
      // a null chain is just "not restored yet" and wiping the key loses the hotel on every refresh.
      if (selectionRestored.current) {
        setSelectedHotel(null);
        selectionStorage.setHotelId(null);
      }
      return;
    }
    hotelService.getAllHotels({ accountid: selectedAccount })
      .then(data => {
        const list = data.hotels || data || [];
        setHotels(list);
        // If stored hotel no longer belongs to this chain, clear it
        const savedHotelId = parseInt(selectionStorage.getHotelId());
        if (savedHotelId && !list.find(h => h.hotelid === savedHotelId)) {
          setSelectedHotel(null);
          selectionStorage.setHotelId(null);
        }
      })
      .catch(() => setHotels([]));
  }, [selectedAccount]);

  // Set the partner WITHOUT selectPartner's side effects.
  //
  // selectPartner() clears the chain and forces an accounts re-fetch, which is right when a user picks a
  // partner from the breadcrumb (the chain list has to change) and catastrophic here — backfilling the
  // partner from the chain you just chose would immediately clear that chain again. This is the quiet
  // half: state plus localStorage, nothing else.
  const adoptPartner = (partnerId) => {
    if (!partnerId) return;
    setSelectedPartner(partnerId);
    selectionStorage.setPartnerId(partnerId);
  };

  // A chain always belongs to exactly one partner, so leaving the breadcrumb on "All Partners" after a
  // chain is chosen shows a state that cannot exist. Fill the partner in from the chain.
  //
  // Only when no partner is already selected: if one is, the chain list was filtered by it and the chain
  // necessarily belongs to it, so there is nothing to correct.
  //
  // partnerId comes from the accounts list (list.js transforms accounts.partnerid -> partnerId). The
  // getMyChains path used by plain chain users does not return it — harmless, because those users have
  // no partner picker to be wrong.
  const selectAccount = (accountId) => {
    setSelectedAccount(accountId);
    // Clear hotel when chain changes
    setSelectedHotel(null);
    selectionStorage.setHotelId(null);
    if (accountId) {
      selectionStorage.setAccountId(accountId);
      if (!selectedPartner) {
        const acct = accounts.find(a => a.id === accountId);
        adoptPartner(acct?.partnerId ?? acct?.partnerid);
      }
    } else {
      selectionStorage.setAccountId(null);
    }
  };

  // Same rule one level down: a hotel belongs to one chain, which belongs to one partner. In the navbar
  // the hotel picker only appears once a chain is chosen, so this is normally a no-op — it matters for a
  // hotel restored from localStorage or reached by a deep link, where the breadcrumb would otherwise
  // name a hotel above "All Hotel Chains".
  const selectHotel = (hotelId) => {
    setSelectedHotel(hotelId);
    if (hotelId) {
      selectionStorage.setHotelId(hotelId);
      if (!selectedAccount) {
        const h = hotels.find(x => x.hotelid === hotelId);
        const acctId = h?.accountid ?? h?.accountId;
        if (acctId) {
          setSelectedAccount(acctId);
          selectionStorage.setAccountId(acctId);
          if (!selectedPartner) {
            const acct = accounts.find(a => a.id === acctId);
            adoptPartner(acct?.partnerId ?? acct?.partnerid);
          }
        }
      }
    } else {
      selectionStorage.setHotelId(null);
    }
  };

  const selectPartner = (partnerId) => {
    if (!partnerId) setPartnerData(null);
    setSelectedPartner(partnerId);
    if (partnerId) {
      selectionStorage.setPartnerId(partnerId);
    } else {
      selectionStorage.setPartnerId(null);
    }
    // Clear selected account and force accounts list re-fetch
    selectAccount(null);
    setAccountsRefreshKey(k => k + 1);
  };

  const triggerAccountsRefresh = () => setAccountsRefreshKey(k => k + 1);

  // Creating a product — a vault, an app instance, a sync project — is an ADMIN act. A permission row
  // carries a role ('A' admin / 'U' user); 'U' is read-and-run on what already exists. Derived once here
  // so every list page, every wizard route and the API agree on one answer: the lambdas apply the same
  // rule (requireCreateAdmin in the util layer), and this is only what stops a user being walked through
  // a wizard that ends in a 403.
  const canCreateProducts = user?.isSuperAdmin === true || isAnyAdmin;
  // A superadmin is known from the token alone, so it needs no round trip; anyone else has to wait.
  const canCreateProductsLoaded = user?.isSuperAdmin === true || adminChecked;

  const value = {
    canCreateProducts,
    canCreateProductsLoaded,
    selectedAccount,
    selectAccount,
    accountData,
    setAccountData,
    accounts,
    setAccounts,
    isChainAdmin,
    isAnyAdmin,
    accountsRefreshKey,
    triggerAccountsRefresh,
    selectedPartner,
    selectPartner,
    partnerData,
    partnerDataLoaded,
    selectedHotel,
    selectHotel,
    hotels,
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
};
